import { OccType } from '@/config/constant';
import BaseService from '@/api/checkInBase';
import ReserveService from '@/api/reserve';
import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import {
  ConfigRoomPrice,
  RentRoomInfo,
  RentTimeInfo,
  RoomCategory,
  RoomExchangeType,
  ExchangeRoomRole,
  CouponValue,
} from '@/redux/reducers/reservation/base.d';
import {
  ReducerState,
  MainState,
  CommonInfo,
  storeName,
  RoomSymbol,
} from '@/redux/reducers/reservation/reserveExchange';

import { ReserveExchangeAction } from './constant';
import {
  createActionMiddleware,
  findItemByKey,
  flatPriceList,
  getEmptyCouponData,
  getBusinessDate,
  formatAssignRoomList,
} from './helper';

// getState函数覆写
const actionWrapper = createActionMiddleware<ReducerState>(storeName);

// 获取可选房间
const getAllRoomList: (timeInfo: RentTimeInfo) => Promise<RoomCategory[]> = async (timeInfo) => {
  let result: RoomCategory[] = [];
  try {
    const { expectedArrivalTime, rentTime, expectedDepartureTime } = timeInfo;
    if (expectedArrivalTime && rentTime) {
      const res = await BaseService.getRoomList({
        expectedArrivalTime: format2TimeStamp(expectedArrivalTime),
        expectedDepartureTime: format2TimeStamp(expectedDepartureTime),
      });
      // result = res.data || [];
      result = formatAssignRoomList(res.data) || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 获取房间定价
const getRoomPriceList: (
  state: ReducerState,
  roomList: RoomCategory[],
) => Promise<ConfigRoomPrice[]> = async (state, roomList) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { channelInfo, rentTimeInfo, commonInfo } = state;
    if (roomList.length && channelInfo.channelId) {
      const roomTypeIds: number[] = []; // 去重的房型列表
      const typeIdMap: { [key: number]: boolean } = {}; // 房型id map
      (roomList || []).forEach((item) => {
        const { id } = item;
        // 已有的房型id
        if (typeIdMap[id]) return;
        roomTypeIds.push(id);
        typeIdMap[id] = true;
      });
      const res = await BaseService.getRoomPrice({
        channelId: channelInfo.channelId,
        endDate: format2DateStr(rentTimeInfo.expectedDepartureTime),
        occDuration: rentTimeInfo.occType === OccType.DayUse ? undefined : rentTimeInfo.rentTime, // 日租房不传
        occType: rentTimeInfo.occType,
        roomTypeIds,
        startDate: format2DateStr(rentTimeInfo.businessDate), // 开始日期要传营业日 营业日小于等于当前日期
        strategyId: channelInfo.strategyId,
        wyuid: commonInfo.ownerWYId,
        memberLevel: commonInfo.memberLevel,
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

// 房间市场价 用来判断房型升降
const getRoomMarketPrice: (state: ReducerState) => Promise<ConfigRoomPrice[]> = async (state) => {
  let result: ConfigRoomPrice[] = [];
  try {
    const { allRoomList, rentRoomInfos } = state;
    const originRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom); // 原房间
    const { rentDate } = originRoomInfo.roomPriceList[0]; // 第一天
    if (allRoomList.length) {
      // 线下渠道日租房第一天的价格
      const res = await ReserveService.getRoomMarketPrice({
        channelType: 'WALK_IN',
        occType: OccType.DayUse,
        priceType: 'MARKET',
        startDate: rentDate,
        endDate: rentDate,
        roomTypeIds: allRoomList.map((item) => item.id),
      });
      result = res.data || [];
    }
  } catch (_) {
    result = [];
  }
  return result;
};

/**
 * 更新loading状态
 * 加个timer防止重复修改时闪烁
 */
let loadingTimer;
export const setLoadingStatus: (isLoading: boolean) => void = (isLoading) => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  return actionWrapper((dispatch, getState) => {
    // 和当前状态不一样
    loadingTimer = setTimeout(() => {
      loadingTimer = null;
      if (getState().isLoading !== isLoading) {
        dispatch({
          type: ReserveExchangeAction.setLoadingStatus,
          payload: isLoading,
        });
      }
    });
  });
};

// 更新初始化状态
const setInitStatus = (isInit: boolean) => {
  return { type: ReserveExchangeAction.setInitStatus, payload: isInit };
};

// 更新数据变更状态
const setModifyStatus = (isModify: boolean) => {
  return { type: ReserveExchangeAction.setModifyStatus, payload: isModify };
};

// 更新可选房间列表
const setAllList = (list: RoomCategory[]) => {
  return {
    type: ReserveExchangeAction.setAllRoomList,
    payload: list,
  };
};

// 更新房价列表
const setPriceList = (list: ConfigRoomPrice[]) => {
  return {
    type: ReserveExchangeAction.setRoomPriceList,
    payload: list,
  };
};

// 更新市场价
const setMarketPrice = (list: ConfigRoomPrice[]) => {
  return {
    type: ReserveExchangeAction.setMarketPriceList,
    payload: list,
  };
};

// 更新入住房间信息
const setRentList: (list: RentRoomInfo[]) => void = (list) => {
  return {
    type: ReserveExchangeAction.setRentRoomList,
    payload: list,
  };
};

// 更新公共信息
const setCommonData: (data: Partial<CommonInfo>) => void = (data) => {
  return { type: ReserveExchangeAction.setCommonData, payload: data };
};

// 刷新可选房间列表
const refreshRoomList = (callback?: () => void) => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { rentTimeInfo } = state;
    const { expectedArrivalTime, rentTime } = rentTimeInfo;
    if (expectedArrivalTime && rentTime) {
      dispatch(setLoadingStatus(true));
      const roomList = await getAllRoomList(rentTimeInfo);
      dispatch(setAllList(roomList));
      dispatch(setLoadingStatus(false));
      if (typeof callback === 'function') {
        callback();
      }
    }
  });
};

// 更新新房间价格信息
const updateTargetRoomPrice = () => {
  return actionWrapper(async (dispatch, getState) => {
    const { rentRoomInfos, commonInfo, roomPriceList } = getState();
    const { exchangeType } = commonInfo;
    // 原价换房 价格和券都和原房间保持一致
    if (exchangeType === RoomExchangeType.KeepSame) {
      const originRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.OriginRoom); // 原房间
      dispatch(
        setRentList(
          rentRoomInfos.map((room) => {
            if (room.itemRole === ExchangeRoomRole.OriginRoom) return room; // 原房间 不做更新
            return {
              ...room,
              deposit: originRoomInfo.deposit,
              roomPriceList: (originRoomInfo.roomPriceList || []).map((item) => {
                return { ...item };
              }),
            };
          }),
        ),
      );
    }
    // 改价换房
    if (exchangeType === RoomExchangeType.Modify) {
      const { cachePriceMap, cacheDepositMap } = flatPriceList(roomPriceList);
      const emptyCouponData = getEmptyCouponData();
      // 更新已选房间房价
      dispatch(
        setRentList(
          rentRoomInfos.map((roomItem) => {
            if (roomItem.itemRole === ExchangeRoomRole.OriginRoom) return roomItem; // 原房间 不做更新
            const itemPriceList = roomItem.roomPriceList || [];
            const typePriceMap = cachePriceMap[roomItem.roomTypeId] || {};
            return {
              ...roomItem,
              deposit: cacheDepositMap[roomItem.roomTypeId],
              roomPriceList: itemPriceList.map((dayItem) => {
                // 已过夜审的 不修改
                if (dayItem.isBeforeAudit) return dayItem;
                return {
                  ...dayItem,
                  // 默认不选择优惠券
                  ...emptyCouponData,
                  inputPrice: typePriceMap[dayItem.rentDate],
                };
              }),
            };
          }),
        ),
      );
    }
  });
};

// 刷新房间定价
const refreshPriceList = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const { allRoomList, rentRoomInfos } = state;
    // 没有选中房间
    if (!rentRoomInfos.length) return;
    dispatch(setLoadingStatus(true));
    const priceList = await getRoomPriceList(state, allRoomList || []);
    dispatch(setPriceList(priceList));
    dispatch(setLoadingStatus(false));
  });
};

// 刷新市场价
const refreshMarketPrice = () => {
  return actionWrapper(async (dispatch, getState) => {
    const state = getState();
    const priceList = await getRoomMarketPrice(state);
    dispatch(setMarketPrice(priceList));
  });
};

// 更新当前营业日
const refreshBusinessDate = () => {
  return actionWrapper(async (dispatch, getState) => {
    dispatch(setLoadingStatus(true));
    const businessDate = await getBusinessDate(DateJS());
    const { rentRoomInfos } = getState();

    dispatch(setCommonData({ businessDate }));
    dispatch(
      setRentList(
        rentRoomInfos.map((room) => {
          return {
            ...room,
            roomPriceList: (room.roomPriceList || []).map((item) => {
              return {
                ...item,
                isBeforeAudit: DateJS(item.rentDate).isBefore(businessDate, 'day'), // 是否过夜审：在当前营业日之前
              };
            }),
          };
        }),
      ),
    );
    dispatch(setLoadingStatus(false));
  });
};

// 页面初始化
export const initPageState: (initFunc: () => Promise<MainState>) => void = (initFunc) => {
  return async (dispatch) => {
    try {
      // 未初始化状态
      dispatch({
        type: ReserveExchangeAction.initDefaultState,
        payload: <ReducerState>{
          isInit: false,
          isLoading: true,
          isModify: false,
          allRoomList: [],
          roomPriceList: [],
          rentRoomInfos: [],
          commonInfo: {},
          rentTimeInfo: {},
          channelInfo: {},
          marketPriceList: [],
        },
      });
      // 调用初始化方法
      const initState = await initFunc();
      // 设置初始化数据
      dispatch({ type: ReserveExchangeAction.initDefaultState, payload: initState });
      dispatch(setInitStatus(true));
      dispatch(refreshBusinessDate());
      // 刷新可选房间列表
      dispatch(
        refreshRoomList(() => {
          dispatch(refreshMarketPrice()); // 刷新市场价
          dispatch(refreshPriceList()); // 刷新价格
        }),
      );
    } catch (_) {
      dispatch(setInitStatus(false));
    }
  };
};

// 入住房间修改
export const handleRentItemChange: (indexId: string, updateValue: Partial<RentRoomInfo>) => void = (
  indexId,
  updateValue,
) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    dispatch(
      setRentList(
        rentRoomInfos.map((item) => {
          if (item.itemRole === ExchangeRoomRole.OriginRoom) return item; // 原房间 不可修改
          if (item.indexId === indexId) {
            return {
              ...item,
              ...updateValue,
            };
          }
          return item;
        }),
      ),
    );
    dispatch(setModifyStatus(true));
  });
};

// 刷新过期房间数据
export const refreshOutdateRoomInfo = () => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos } = getState();
    // 新房间
    const targetRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.TargetRoom);
    // 将新房间置空
    dispatch(
      handleRentItemChange(targetRoomInfo.indexId, {
        roomId: RoomSymbol.Block,
        roomNo: null,
      }),
    );
    dispatch(refreshRoomList());
  });
};

// 新房间类型修改
export const handleTargetRoomTypeChange: (roomTypeId: RoomExchangeType) => void = (roomTypeId) => {
  return actionWrapper((dispatch, getState) => {
    const { rentRoomInfos, allRoomList } = getState();
    const targetType = findItemByKey(allRoomList, 'id', roomTypeId);
    // 新房间
    const targetRoomInfo = findItemByKey(rentRoomInfos, 'itemRole', ExchangeRoomRole.TargetRoom);
    // 新房型默认为待排房
    dispatch(
      handleRentItemChange(targetRoomInfo.indexId, {
        roomTypeId: targetType.id,
        roomTypeName: targetType.name,
        roomId: RoomSymbol.Block,
        roomNo: null,
      }),
    );
    dispatch(updateTargetRoomPrice());
  });
};

// 房价变更类型修改
export const handleExchangeTypeChange: (type: RoomExchangeType) => void = (type) => {
  return actionWrapper((dispatch) => {
    dispatch(setCommonData({ exchangeType: type }));
    dispatch(updateTargetRoomPrice());
  });
};

// 批量修改房间券
export const batchUpdateRoomCoupon = (data: Record<string, Record<string, CouponValue>>) => {
  return actionWrapper((dispatch, getState) => {
    const { commonInfo, rentRoomInfos } = getState();
    if (commonInfo.exchangeType === RoomExchangeType.KeepSame) return; // 原价换房 不可修改
    // 更新券
    dispatch(
      setRentList(
        rentRoomInfos.map((roomItem) => {
          const roomCouponMap = data[roomItem.indexId];
          if (!roomCouponMap) return roomItem;
          const itemPriceList = roomItem.roomPriceList || [];
          return {
            ...roomItem,
            roomPriceList: itemPriceList.map((dayItem) => {
              if (dayItem.isBeforeAudit) return dayItem; // 已过夜审 房价和券不可修改
              // 当前间夜券有调整
              if (roomCouponMap[dayItem.rentDateId]) {
                return {
                  ...dayItem,
                  ...roomCouponMap[dayItem.rentDateId],
                };
              }
              return dayItem;
            }),
          };
        }),
      ),
    );
  });
};

// 获取全部状态
export const getMainState: (callback: (state: ReducerState) => void) => void = (callback) => {
  return actionWrapper((_, getState) => {
    try {
      callback(getState());
    } catch (e) {
      //
    }
  });
};
